import PropTypes from 'prop-types';
import React from 'react';

import cls from 'classnames/bind';

import { Auth } from '@ott/auth';
import Button from '@ott/button';
import { lang, l10n } from '@ott/l10n';
import { withLayoutContextForwardRef } from '@ott/layout-context';
import LoadingDots from '@ott/loading-dots';
import { helpers as virtualCardHelpers } from '@ott/shared-virtual-card-logic';

import HeaderAuthIcon from './blocks/HeaderAuthIcon';
import HeaderProfileIcon from './blocks/HeaderProfileIcon';
import PremiumStatusBadgeIcon from './blocks/PremiumStatusBadgeIcon';
import ProStatusBadgeIcon from './blocks/ProStatusBadgeIcon';
import { MENU_ITEMS, POSITION, STATUSES } from './constants/common';
import ITEMS_CONFIG from './constants/items';
import headerAuthHelpers from './helpers';
import { isPremiumStatus, isAuthenticated, isLoading, getName } from './utility';

import styles from './HeaderAuth.scss';

const cx = cls.bind(styles);

export { MENU_ITEMS, POSITION, headerAuthHelpers };

const SQUEEZE_AUTH_BREAKPOINT = parseInt(styles.squeezeAuthBreakPoint);

class HeaderAuth extends React.PureComponent {
  static propTypes = {
    auth: PropTypes.shape({
      data: PropTypes.object,
      infoStatus: PropTypes.shape({
        initial: PropTypes.bool,
        success: PropTypes.bool,
      }),
      enterStatus: PropTypes.oneOfType([
        PropTypes.shape({
          success: PropTypes.bool,
          loading: PropTypes.bool,
        }),
        PropTypes.bool,
      ]),
      exitStatus: PropTypes.oneOfType([
        PropTypes.shape({
          success: PropTypes.bool,
        }),
        PropTypes.bool,
      ]),
    }).isRequired,
    authActions: PropTypes.shape({
      exit: PropTypes.func,
      regReset: PropTypes.func,
      getUserInfo: PropTypes.func,
      showAuth: PropTypes.func.isRequired,
      hideAuth: PropTypes.func.isRequired,
    }).isRequired,
    authModal: PropTypes.shape({
      show: PropTypes.bool.isRequired,
      formType: PropTypes.string,
    }).isRequired,

    // @ott/layout-context props here
    isMobileLayout: PropTypes.bool,
    isTabletLayout: PropTypes.bool,

    // Items displayed alongside the auth element
    menuItemsConfig: PropTypes.shape({
      authorized: PropTypes.oneOfType([
        PropTypes.shape({
          desktop: PropTypes.arrayOf(PropTypes.string),
          mobile: PropTypes.arrayOf(PropTypes.string),
        }),
        PropTypes.arrayOf(PropTypes.string),
      ]),
      notAuthorized: PropTypes.oneOfType([
        PropTypes.shape({
          desktop: PropTypes.arrayOf(PropTypes.string),
          mobile: PropTypes.arrayOf(PropTypes.string),
        }),
        PropTypes.arrayOf(PropTypes.string),
      ]),
    }),

    // Items displayed in the menu dropdown
    dropdownMenuItemsConfig: PropTypes.shape({
      authorized: PropTypes.oneOfType([
        PropTypes.shape({
          desktop: PropTypes.arrayOf(PropTypes.string),
          mobile: PropTypes.arrayOf(PropTypes.string),
        }),
        PropTypes.arrayOf(PropTypes.string),
      ]),
      notAuthorized: PropTypes.oneOfType([
        PropTypes.shape({
          desktop: PropTypes.arrayOf(PropTypes.string),
          mobile: PropTypes.arrayOf(PropTypes.string),
        }),
        PropTypes.arrayOf(PropTypes.string),
      ]),
    }),
    withSocialAuth: PropTypes.bool,
    shouldRefetchUserInfo: PropTypes.bool,
    // set false for partners and other locales than 'ru'
    shouldDisplayStatusBadge: PropTypes.bool,
    // set true for partners header
    useHeaderPartnerStyles: PropTypes.bool,
    // set false in case several header-auth components
    // are within a single page
    useComponentAuth: PropTypes.bool,
    partner: PropTypes.object,
    hasProfile: PropTypes.bool,
    position: PropTypes.oneOf(Object.values(POSITION)),

    specificLocators: PropTypes.shape({
      menuItems: PropTypes.shape(
        Object.keys(MENU_ITEMS).reduce((types, item) => {
          types[item] = PropTypes.string;
          return types;
        }, {})
      ),
      authLabelAuthorized: PropTypes.string,
      authLabelUnauthorized: PropTypes.string,
    }),

    childrenClassNames: PropTypes.shape({
      authClassNames: Auth.propTypes.childrenClassNames,
      profileMenuClassName: PropTypes.string,
    }),
  };

  static defaultProps = {
    withSocialAuth: true,
    hasProfile: true,
    shouldRefetchUserInfo: true,
    shouldDisplayStatusBadge: true,
    useComponentAuth: true,
    useHeaderPartnerStyles: false,
    position: POSITION.inplace,
    specificLocators: {},
    childrenClassNames: {},
  };

  constructor(props) {
    super(props);
    this.mobileMenuRef = React.createRef();
    this.profileRef = React.createRef();
    this.menuAuthRef = React.createRef();
  }

  state = { isMenuOpen: false };

  componentDidUpdate(prevProps) {
    const {
      auth: {
        enterStatus: prevEnterStatus,
        exitStatus: prevExitStatus,
        infoStatus: prevInfoStatus,
      },
    } = prevProps;
    const {
      auth: {
        enterStatus: currentEnterStatus,
        exitStatus: currentExitStatus,
        infoStatus: currentInfoStatus,
      },
      shouldRefetchUserInfo,
    } = this.props;

    if (
      !this.props.auth.infoStatus.loading &&
      ((prevEnterStatus.loading && !currentEnterStatus) ||
        (!prevExitStatus.success && currentExitStatus.success) ||
        (!prevInfoStatus.success && currentInfoStatus.success))
    ) {
      this.handleSignInHide(false);

      // refetch user info on logout
      if (shouldRefetchUserInfo && !currentInfoStatus.success) {
        this.props.authActions.getUserInfo();
      }
    }
  }

  handleClickOutside = (event) => {
    const { isMenuOpen } = this.state;

    const { current: mobileMenuEl } = this.mobileMenuRef;
    const { current: menuAuthEl } = this.menuAuthRef;
    const { current: profileEl } = this.profileRef;

    const isExistedMobileMenuAuth = mobileMenuEl && !mobileMenuEl.contains(event.target);
    const isExistedMobileMenuUnAuth = menuAuthEl && !menuAuthEl.contains(event.target);

    if (isExistedMobileMenuAuth || isExistedMobileMenuUnAuth) {
      if (profileEl && profileEl.contains(event.target) && isMenuOpen) {
        return; // клик по профилю -> пропускаем тут -> сработает toogle
      }
      this.setState({ isMenuOpen: false });
    }
  };

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside, true);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside, true);
    window.removeEventListener('click', this.handleClick);
  }

  setContentRef = (el) => {
    this.contentRef = el;
  };

  handleClick = (e) => {
    if (this.isLoadingAuthInfo()) {
      return;
    }

    const { isMenuOpen } = this.state;
    this.setState({ isMenuOpen: !isMenuOpen });
  };

  handleSignInShow = (e) => {
    const {
      authActions: { showAuth },
    } = this.props;

    if (this.isLoadingAuthInfo()) {
      return;
    }

    e.preventDefault();
    this.setState({ isMenuOpen: false });
    showAuth();
  };

  handleExit = (e) => {
    const {
      authActions: { exit },
    } = this.props;
    e.preventDefault();
    exit();
  };

  handleSignInHide = (isHideByUser) => {
    const {
      authActions: { hideAuth, regReset },
    } = this.props;

    this.setState({ isMenuOpen: false });
    hideAuth(isHideByUser);
  };

  isLoadingAuthInfo = () => {
    const {
      auth: { infoStatus, exitStatus, enterStatus },
    } = this.props;

    return (
      (infoStatus.loading || exitStatus.loading || enterStatus.loading) &&
      !exitStatus.success &&
      !enterStatus.error
    );
  };

  getMenuItems = (menuItemsConfig) => {
    const { auth, partner, isMobileLayout, isTabletLayout, isMobile, viewportWidth } = this.props;
    // Get the first level category by auth state
    const menuItemsCategory = isAuthenticated(auth) ? 'authorized' : 'notAuthorized';
    const configByCategory = menuItemsConfig[menuItemsCategory];

    // Get the second level category by adaptivity state
    const hasAdaptiveConfig =
      configByCategory.hasOwnProperty('mobile') && configByCategory.hasOwnProperty('desktop');
    const adaptiveCategory = viewportWidth < SQUEEZE_AUTH_BREAKPOINT ? 'mobile' : 'desktop';

    const dropdownMenuItemsList = hasAdaptiveConfig
      ? configByCategory[adaptiveCategory]
      : configByCategory;

    return dropdownMenuItemsList
      .filter(headerAuthHelpers.filterItem({ auth, partner, lang }))
      .map((menuItemName) => this.getMenuItemConfig(menuItemName));
  };

  getMenuItemConfig = (menuItemName) => {
    return {
      name: menuItemName,
      config: ITEMS_CONFIG[menuItemName],
    };
  };

  getMenuItemLocator = ({ config } = {}) => {
    const {
      specificLocators: { menuItems = {} },
    } = this.props;
    if (config) {
      return menuItems[config.key] || config.locator || config.key;
    }

    return '';
  };

  renderLoadingDots = () => {
    const { useHeaderPartnerStyles } = this.props;

    return (
      <div className={cx('loadingDotsContainer')}>
        <LoadingDots isWhite={!useHeaderPartnerStyles} />
      </div>
    );
  };

  renderLogin = (item, itemClassName) => {
    const {
      auth: { infoStatus, exitStatus, enterStatus },
      partner,
      hasProfile,
    } = this.props;

    if (!hasProfile || (partner && partner.disableProfile)) {
      return null;
    }

    return (
      <Button
        size="medium"
        purpose="neutral"
        className={cx('login', 'menu-item', itemClassName)}
        key={item.config.key}
        data-href={`//m.onetwotrip.com/${lang}/auth/`}
        onClick={this.handleSignInShow}
        specificLocators={this.getMenuItemLocator(item)}
      >
        {this.isLoadingAuthInfo() ? this.renderLoadingDots() : l10n(item.config.l10nKey)}
      </Button>
    );
  };

  renderLogout = (item, itemClassName) => {
    const { hasProfile, partner } = this.props;

    if (!hasProfile || (partner && partner.disableProfile)) {
      return null;
    }

    return (
      <span
        key={item.config.key}
        className={cx('menu-item', itemClassName)}
        onClick={this.handleExit}
        data-locator={this.getMenuItemLocator(item)}
      >
        {l10n(item.config.l10nKey)}
      </span>
    );
  };

  renderStatusBadge = () => {
    const {
      auth: {
        data: {
          userStatus: { type: userStatus },
        },
      },
    } = this.props;

    switch (userStatus) {
      case STATUSES.pro:
        return (
          <ProStatusBadgeIcon className={cx('statusBadge')} specificLocators="pro-status-badge" />
        );
      case STATUSES.premium:
        return (
          <PremiumStatusBadgeIcon
            className={cx('statusBadge')}
            specificLocators="premium-status-badge"
          />
        );
      default:
        return null;
    }
  };

  renderMobileLogin = (item, itemClassName) => {
    const {
      auth: { infoStatus, exitStatus, enterStatus },
      hasProfile,
      partner,
    } = this.props;

    if (!hasProfile || (partner && partner.disableProfile)) {
      return null;
    }

    return (
      <span
        key={item.config.key}
        className={cx('menu-item', item.config.key, itemClassName)}
        data-locator={this.getMenuItemLocator(item)}
        onClick={this.handleSignInShow}
      >
        {l10n(item.config.l10nKey)}
      </span>
    );
  };

  renderContentNode = () => {
    const {
      auth,
      partner,
      shouldDisplayStatusBadge,
      specificLocators: { authLabelAuthorized, authLabelUnauthorized },
      childrenClassNames,
    } = this.props;
    const { isMenuOpen } = this.state;

    if (isAuthenticated(auth)) {
      const {
        data: {
          userStatus: { type: userStatus },
        },
      } = auth;

      const displayStatusBadge =
        !partner &&
        shouldDisplayStatusBadge &&
        lang === 'ru' &&
        (userStatus === STATUSES.pro || userStatus === STATUSES.premium);

      const profileLabelNode = displayStatusBadge ? (
        <div key="profileLabel" className={cx('profileLabel', `profileLabel--${userStatus}`)}>
          {this.renderStatusBadge()}
          <HeaderProfileIcon className={cx('icon')} />
        </div>
      ) : (
        <div key="profileLabel" className={cx('profileLabel')}>
          <HeaderProfileIcon className={cx('icon')} />
        </div>
      );

      return [
        <div key="menuItems" className={cx('menuItems')}>
          <div
            data-locator={cls('authLabel', authLabelAuthorized, {
              'no-status-badge': !displayStatusBadge,
            })}
            className={cx('profileWrapper')}
            onClick={this.handleClick}
            key="profileWrapper"
            ref={this.profileRef}
          >
            {profileLabelNode}
          </div>
          <aside
            key="profileMenu"
            data-locator="profile-menu"
            className={cx('profileMenu', {
              'profileMenu--isMenuOpen': isMenuOpen,
            })}
            ref={this.mobileMenuRef}
          >
            {this.renderDropdownMenuItems()}
          </aside>
        </div>,
      ];
    }

    return [
      <div
        key="menuItems"
        className={cx('menuItems', {
          'menuItems--isMenuAuthOpen': isMenuOpen,
        })}
        ref={this.menuAuthRef}
      >
        {this.renderMenuItems()}
        <div
          key="authLabel"
          className={cx('authLabel')}
          data-locator={cls('authLabel', authLabelUnauthorized)}
          onClick={this.handleClick}
        >
          {this.isLoadingAuthInfo() ? (
            this.renderLoadingDots()
          ) : (
            <HeaderAuthIcon className={cx('icon')} />
          )}
        </div>
        <aside
          key="menuAuth"
          className={cx('menuAuth', childrenClassNames.profileMenuClassName, {
            'menuAuth--isMenuOpen': isMenuOpen,
          })}
        >
          {this.renderDropdownMenuItems()}
        </aside>
      </div>,
    ];
  };

  renderDropdownMenuItems = () => {
    const { dropdownMenuItemsConfig = headerAuthHelpers.getProductsDropdownMenuItemsConfig() } =
      this.props;
    const menuItems = this.getMenuItems(dropdownMenuItemsConfig);
    return menuItems.map(this.menuItemRenderer);
  };

  renderMenuItems = () => {
    const { menuItemsConfig = headerAuthHelpers.getProductsMenuItemsConfig() } = this.props;
    const menuItems = this.getMenuItems(menuItemsConfig);
    return menuItems.map(this.menuItemRenderer);
  };

  getHrefLink = (item) => {
    let href = `/${lang}${item.config.url}`;

    if (typeof window !== 'undefined') {
      if (window.location.hostname.includes('.kz')) {
        const hostname = window.location.hostname.replace('.kz', '.com');
        href = `${window.location.protocol}//${hostname}/${lang}${item.config.url}`;
      }
    }

    return href;
  };

  getVirtualCardUrl = (config) => {
    const {
      auth: { data: { cards = [] } = {} },
    } = this.props;

    const { url, authorizedUrl } = config;
    const userHasVirtualCard = virtualCardHelpers.hasVirtualCard(cards);

    return userHasVirtualCard ? authorizedUrl : url;
  };

  renderLinkMenuItem = (item, menuItemClass) => {
    return (
      <a
        key={item.config.key}
        className={cx('menu-item', item.config.key, menuItemClass)}
        target="_blank"
        href={this.getHrefLink(item)}
        data-locator={this.getMenuItemLocator(item)}
      >
        {l10n(item.config.l10nKey)}
      </a>
    );
  };

  menuItemRenderer = (item) => {
    const { isMobileLayout, isTabletLayout, isMobile } = this.props;
    const adaptiveCategory = isMobile ? 'mobile' : 'desktop';
    const menuItemClass = `menu-item--${adaptiveCategory}`;

    switch (item.name) {
      case MENU_ITEMS.login:
      case MENU_ITEMS.profileLogin: {
        return this.renderLogin(item, menuItemClass);
      }
      case MENU_ITEMS.exit: {
        return this.renderLogout(item, menuItemClass);
      }
      case MENU_ITEMS.mobileLogin: {
        return this.renderMobileLogin(item, menuItemClass);
      }
      case MENU_ITEMS.virtualcard: {
        const customItem = {
          ...item,
          config: {
            ...item.config,
            url: this.getVirtualCardUrl(item.config),
          },
        };

        return this.renderLinkMenuItem(customItem, menuItemClass);
      }
      case MENU_ITEMS.order:
      case MENU_ITEMS.profile:
      case MENU_ITEMS.achievements:
      case MENU_ITEMS.travelexpert: {
        return this.renderLinkMenuItem(item, menuItemClass);
      }
    }

    return null;
  };

  renderAuthModalNode() {
    const {
      authModal: { show, formType },
      withSocialAuth,
      useComponentAuth,
      childrenClassNames,
    } = this.props;

    if (!show || !useComponentAuth) {
      return null;
    }

    return (
      <Auth
        withSocialAuth={withSocialAuth}
        formType={formType}
        onClose={this.handleSignInHide}
        childrenClassNames={childrenClassNames.authClassNames}
      />
    );
  }

  render() {
    const { className, partner, position, useHeaderPartnerStyles } = this.props;

    return (
      <div
        key="headerAuth"
        className={cls(className, styles.HeaderAuth, styles[`HeaderAuth--${position}`], {
          [styles['HeaderAuth--partner']]: Boolean(partner) && useHeaderPartnerStyles,
        })}
        ref={this.setContentRef}
      >
        {this.renderContentNode()}
        {this.renderAuthModalNode()}
      </div>
    );
  }
}

export default withLayoutContextForwardRef(HeaderAuth);
